import React, { useState } from 'react';
import { MessageCircle, X, Lightbulb, Bug, Heart, HelpCircle, Send, CheckCircle } from 'lucide-react';
import { submitFeedback } from '../services/apiClient';

const OPTIONS = [
  { key: 'idea', label: 'Idea', icon: Lightbulb },
  { key: 'bug', label: 'Bug', icon: Bug },
  { key: 'love', label: 'Love it', icon: Heart },
  { key: 'confused', label: 'Confused', icon: HelpCircle },
];

export default function FeedbackWidget({ user, siteMode }) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('idea');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [website, setWebsite] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [shotError, setShotError] = useState('');
  const isTester = Boolean(user?.permissions?.isTester);

  function handleScreenshot(event) {
    setShotError('');
    const file = event.target.files?.[0];
    if (!file) { setScreenshot(null); return; }
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) {
      setShotError('Please choose a PNG, JPG, WEBP, or GIF image.');
      return;
    }
    if (file.size > 2_400_000) {
      setShotError('Screenshot is too large (max ~2.4MB). Try cropping or compressing it.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setScreenshot(reader.result);
    reader.onerror = () => setShotError('Could not read that image.');
    reader.readAsDataURL(file);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    if (!message.trim()) {
      setError('Please add a short note before sending feedback.');
      return;
    }
    setSubmitting(true);
    const result = await submitFeedback({
      type,
      message,
      page: window.location.pathname + window.location.search,
      pageTitle: document.title || 'Bond Mentor',
      mode: siteMode?.effectiveMode || siteMode?.mode || 'live',
      screenshot: isTester ? screenshot : null,
      important: isTester,
      website,
    });
    setSubmitting(false);
    if (result?.ok) {
      setSubmitted(true);
      setMessage('');
      setScreenshot(null);
      return;
    }
    setError(result?.message || 'Feedback could not be sent. Please try again.');
  }

  function closePanel() {
    setOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setError('');
    }, 250);
  }

  return (
    <div className="fixed bottom-5 left-5 z-[90] print:hidden">
      {open && (
        <div className="mb-3 w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-mentor-border/70 bg-mentor-surface shadow-2xl shadow-black/40">
          <div className="flex items-center justify-between bg-mentor-accent px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <MessageCircle size={17} />
              <h3 className="font-display text-sm font-bold">Send Feedback</h3>
            </div>
            <button type="button" onClick={closePanel} className="rounded-full p-1 hover:bg-white/15" aria-label="Close feedback">
              <X size={16} />
            </button>
          </div>

          {submitted ? (
            <div className="px-5 py-8 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-mentor-accent/10 text-mentor-accent">
                <CheckCircle size={24} />
              </div>
              <h4 className="font-display text-lg font-bold text-mentor-text">Thank you!</h4>
              <p className="mt-2 text-sm leading-relaxed text-mentor-muted">
                We read every piece of feedback. Your input shapes what we build next.
              </p>
              <button
                type="button"
                onClick={closePanel}
                className="mt-5 rounded-xl bg-mentor-accent px-4 py-2 text-sm font-display font-bold text-white hover:bg-mentor-accent/90"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4">
              <div className="grid grid-cols-2 gap-2">
                {OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const active = type === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setType(option.key)}
                      className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-xs font-display font-bold transition-all ${active ? 'border-mentor-accent bg-mentor-accent/15 text-mentor-text' : 'border-mentor-border/60 text-mentor-muted hover:border-mentor-accent/50 hover:text-mentor-text'}`}
                    >
                      <Icon size={14} />
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <label className="sr-only" htmlFor="bond-mentor-feedback-message">Feedback message</label>
              <textarea
                id="bond-mentor-feedback-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                rows={5}
                maxLength={2000}
                placeholder="Tell us what you noticed, loved, found confusing, or want us to build next..."
                className="mt-3 w-full resize-none rounded-xl border border-mentor-border/60 bg-mentor-bg px-3 py-3 text-sm text-mentor-text placeholder:text-mentor-muted/70 focus:border-mentor-accent/60 focus:outline-none"
              />

              <input
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
                value={website}
                onChange={(event) => setWebsite(event.target.value)}
                aria-hidden="true"
              />

              {error && <p className="mt-2 rounded-lg border border-signal-red/30 bg-signal-red/10 px-3 py-2 text-xs text-red-200">{error}</p>}

              {isTester && (
                <div className="mt-3 rounded-xl border border-mentor-accent/30 bg-mentor-accent/[0.06] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-display font-bold uppercase tracking-wider text-mentor-accent">QA Tester</span>
                    <span className="text-[10px] text-mentor-muted">Flagged Important to admin</span>
                  </div>
                  <label className="text-xs text-mentor-muted block mb-1">Attach a screenshot (optional)</label>
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleScreenshot}
                    className="block w-full text-xs text-mentor-muted file:mr-3 file:rounded-lg file:border-0 file:bg-mentor-accent/20 file:px-3 file:py-1.5 file:text-mentor-accent file:font-display file:font-bold" />
                  {shotError && <p className="mt-1 text-[11px] text-red-300">{shotError}</p>}
                  {screenshot && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={screenshot} alt="Screenshot preview" className="h-12 w-12 rounded-lg object-cover border border-mentor-border/60" />
                      <button type="button" onClick={() => setScreenshot(null)} className="text-[11px] text-mentor-muted hover:text-mentor-text underline">Remove</button>
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-mentor-accent px-4 py-3 font-display text-sm font-bold text-white shadow-lg shadow-mentor-accent/20 transition-all hover:bg-mentor-accent/90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send size={15} />
                {submitting ? 'Sending...' : 'Send Feedback'}
              </button>

              <p className="mt-2 text-center text-[10px] leading-relaxed text-mentor-muted">
                {user ? 'We will attach your account so the admin can follow up if needed.' : 'Anonymous feedback is welcome during public pages and soft launch.'}
              </p>
            </form>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex h-12 w-12 items-center justify-center rounded-full border border-mentor-accent/40 bg-mentor-accent text-white shadow-xl shadow-black/30 transition-all hover:scale-105 hover:bg-mentor-accent/90"
        aria-label="Open feedback"
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
}
